import { api, ApiErrorResponse, sessionAccountId, setDeviceUnknownHandler } from "./api";
import type { RequestBody, Result } from "./spec";
import { deviceKey, forgetStoredDevice, storedDeviceId } from "./device-id";
import { setItem } from "./storage";

export { deviceKey, storedDeviceId };

/*
  The Device: this browser, as the account's list of "where am I signed in" knows
  it. One per browser sign-in, however many tabs are open.

  The id lives in localStorage, which every tab of the browser shares, under a key
  that is *not* an `astring-` preference: it belongs to the session, so sign-out
  wipes it with the tokens. It is stored with the account it was registered for,
  because a Device is one account's, and an id read back for another is not ours.

  Two tabs mounting at once would both find nothing stored and both register. A Web
  Lock around "read, else register and store" lets the first tab do it and shows the
  second what it stored. Without Web Locks the tab still dedupes its own callers.
*/

const lockName = "astring-device-registration";

export interface LockManagerLike {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}

export interface EnsureDeviceDeps {
  /** `null` is a browser without Web Locks. Defaults to `navigator.locks`. */
  locks?: LockManagerLike | null;
  /** What this browser calls itself. Defaults to the user agent, read as "Chrome on macOS". */
  describe?: () => string;
}

/** Drops the id, so the next `ensureDevice` registers afresh. */
export const forgetDevice = forgetStoredDevice;

const browserLocks = (): LockManagerLike | null => {
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  return locks
    ? { request: (name, callback) => locks.request(name, callback) as ReturnType<typeof callback> }
    : null;
};

/** "Chrome on macOS": the browser and the system it runs on, as a person would say them. */
export const describeBrowser = (
  agent: string = typeof navigator === "undefined" ? "" : navigator.userAgent
): string => {
  // Order matters: Edge and Opera also say Chrome, and Chrome also says Safari.
  const browser = [
    [/Edg(e|A|iOS)?\//, "Edge"],
    [/OPR\/|Opera/, "Opera"],
    [/Firefox\/|FxiOS\//, "Firefox"],
    [/Chrome\/|CriOS\//, "Chrome"],
    [/Safari\//, "Safari"],
  ].find(([re]) => (re as RegExp).test(agent))?.[1] as string | undefined;
  // iOS says "like Mac OS X" and Android says "Linux", so both come first.
  const system = [
    [/iPhone|iPad|iPod/, "iOS"],
    [/Android/, "Android"],
    [/Windows/, "Windows"],
    [/Mac OS X|Macintosh/, "macOS"],
    [/CrOS/, "ChromeOS"],
    [/Linux|X11/, "Linux"],
  ].find(([re]) => (re as RegExp).test(agent))?.[1] as string | undefined;
  if (browser && system) return `${browser} on ${system}`;
  return browser ?? system ?? "Web browser";
};

const inFlight = new Map<string, Promise<string | undefined>>();

/**
 * The id of this browser's Device for `accountId`, registering it if there is none
 * yet. Never rejects: a Device is not worth failing a screen over, and the next call
 * simply tries again.
 */
export function ensureDevice(accountId: string, deps: EnsureDeviceDeps = {}): Promise<string | undefined> {
  const existing = inFlight.get(accountId);
  if (existing) return existing;
  const locks = deps.locks === undefined ? browserLocks() : deps.locks;
  // The session this call serves. A registration is slow, and a sign-out (or another
  // account signing in) that lands meanwhile must not get a `device_id` written
  // after the wipe: logout would then name a Device that is not the session's.
  const session = sessionAccountId();
  const register = async () => {
    const known = storedDeviceId(accountId);
    if (known) return known;
    const device = await api.post("device/register", {
      body: { platform: "web", name: (deps.describe ?? describeBrowser)() },
      hasAuth: true,
    });
    const id = device?.id;
    if (sessionAccountId() !== session) return undefined;
    if (id) setItem(deviceKey, JSON.stringify({ accountId, id }));
    return id;
  };
  const run = (locks ? locks.request(lockName, register) : register())
    .catch((error) => {
      console.warn("device registration failed", error);
      return undefined;
    })
    .finally(() => inFlight.delete(accountId));
  inFlight.set(accountId, run);
  return run;
}

// A refresh that finds its Device gone forgets the id; this registers the new one.
setDeviceUnknownHandler((accountId) => void ensureDevice(accountId));

/** Stringr answers 404 for a Device that is not the caller's or is gone. */
const isGone = (error: unknown) =>
  error instanceof ApiErrorResponse && (error.httpStatus ?? error.statusCode) === 404;

export type DeviceTokenReq = RequestBody<"notification/token", "post">;
export type Device = NonNullable<Result<"device/list", "get">["items"]>[number];

export const deviceService = {
  list: (deviceId?: string) =>
    api.get("device/list", { hasAuth: true, params: deviceId ? { deviceId } : {} }),
  remove: (id: string) => api.del("device/{id}", { path: { id }, hasAuth: true }),
};

/**
 * Sends this browser's push token with its Device id. A Device the server no longer
 * knows is registered afresh and the token sent again, never surfaced as an error;
 * with no Device obtainable at all the token goes without one, as it always did.
 */
export async function registerPushToken(
  accountId: string,
  body: DeviceTokenReq,
  deps: EnsureDeviceDeps = {}
) {
  const send = async (deviceId: string | undefined) =>
    api.post("notification/token", { body: deviceId ? { ...body, deviceId } : body, hasAuth: true });
  const deviceId = await ensureDevice(accountId, deps);
  try {
    return await send(deviceId);
  } catch (error) {
    if (!deviceId || !isGone(error)) throw error;
    forgetDevice();
    return send(await ensureDevice(accountId, deps));
  }
}

/**
 * The account's Devices with this browser's marked `current`. A stored id the server
 * no longer lists (removed from another browser) is replaced by a fresh registration,
 * so this browser always finds itself in the list.
 */
export async function loadDevices(accountId: string, deps: EnsureDeviceDeps = {}): Promise<Device[]> {
  const fetch = async () => {
    const id = await ensureDevice(accountId, deps);
    const items = (await deviceService.list(id))?.items ?? [];
    return { id, items };
  };
  const first = await fetch();
  if (!first.id || first.items.some((d) => d.current)) return first.items;
  forgetDevice();
  return (await fetch()).items;
}
